import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

const STORAGE_PREFIX = "sd:onboarding-platform-ui:";

interface IPersisted {
  sidebarDismissed?: boolean;
  cardExpanded?: boolean;
}

const storageKey = (userId: string) => `${STORAGE_PREFIX}${userId}`;

const read = (userId: string): IPersisted => {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
      return {};
    return parsed as IPersisted;
  } catch {
    return {};
  }
};

const write = (userId: string, value: IPersisted) => {
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(value));
  } catch {
    // Best-effort; a full or blocked store must not break the UI.
  }
};

interface IState extends IPersisted {
  userId: string | null;
}

interface IActions {
  hydrate: (userId: string) => void;
  patch: (value: IPersisted) => void;
}

export const usePlatformUiStore = create<IState & IActions>((set, get) => ({
  userId: null,
  sidebarDismissed: false,
  cardExpanded: false,

  hydrate: (userId) => {
    if (get().userId === userId) return;
    // set() shallow-merges, so a key missing from the new user's record would
    // otherwise keep the previous user's value. Reset to defaults first so an
    // account switch never leaks flags across users.
    set({
      userId,
      sidebarDismissed: false,
      cardExpanded: false,
      ...read(userId),
    });
  },

  patch: (value) => {
    const { userId, sidebarDismissed, cardExpanded } = get();
    if (!userId) {
      set({ sidebarDismissed, cardExpanded, ...value });
      return;
    }
    // Re-read and merge on every write so a second tab, which may hold a
    // stale in-memory snapshot, can never clobber a flag it hasn't seen.
    const next = {
      sidebarDismissed,
      cardExpanded,
      ...read(userId),
      ...value,
    };
    set(next);
    write(userId, next);
  },
}));

export const usePlatformUiStoreShallow = <U>(
  selector: (s: IState & IActions) => U,
) => usePlatformUiStore(useShallow(selector));
